import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * Global keyboard shortcuts.
 *
 * Phase 12.1 — strict 2-key invariant. ZERO 3-key combos anywhere.
 *
 * Guarded (do nothing when typing in input/textarea/contenteditable):
 *   g h | g a | g n   — navigate (2-char sequence, no modifiers)
 *   /                 — focus analyses search input
 *   ?                 — open shortcuts cheat-sheet
 *
 * Cmd/Ctrl + K (command palette) and the rest of the modifier-based
 * shortcuts are handled by the global listener in lib/shortcuts.js /
 * App.js, not here.
 *
 * Issue #43 — removed the Counsel-specific Escape handling
 * (`isCounselInputFocused`/`isCounselOpen`) and the `onToggleCounsel`/
 * `onCloseCounsel`/`onMinimizeCounsel` callbacks: the Counsel widget
 * has never actually been mounted anywhere in the render tree since
 * its Phase I retirement (confirmed — `[data-testid='counsel-widget']`
 * matches zero elements), so `isCounselOpen()` could never return
 * true and this branch was unreachable dead code, not a real feature.
 */
export default function useKeyboardShortcuts({
    onOpenCheatSheet,
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const buffer = useRef({ key: null, ts: 0 });

    useEffect(() => {
        function isTypingNode(node) {
            if (!node) return false;
            const tag = (node.tagName || "").toLowerCase();
            return (
                tag === "input" ||
                tag === "textarea" ||
                tag === "select" ||
                node.isContentEditable === true
            );
        }
        function typing() {
            return isTypingNode(document.activeElement);
        }

        function handler(e) {
            const key = e.key;

            // Bail on remaining modifier presses to avoid swallowing browser shortcuts.
            if (e.metaKey || e.ctrlKey || e.altKey) return;

            // ---- Guarded shortcuts (only when not typing) ----
            if (typing() || isTypingNode(e.target)) return;

            if (key === "?" || (e.shiftKey && key === "/")) {
                e.preventDefault();
                buffer.current.key = null;
                onOpenCheatSheet?.();
                return;
            }
            if (key === "/") {
                const input = document.querySelector('[data-testid="analyses-search"]');
                if (input) {
                    e.preventDefault();
                    input.focus();
                }
                buffer.current.key = null;
                return;
            }
            const now = Date.now();
            if (buffer.current.key === "g" && now - buffer.current.ts < 1500) {
                buffer.current.key = null;
                switch (key) {
                    case "h":
                        e.preventDefault(); navigate("/"); return;
                    case "a":
                        e.preventDefault(); navigate("/analyses"); return;
                    case "n":
                        e.preventDefault(); navigate("/analyses/new"); return;
                    default: return;
                }
            }
            if (key === "g") buffer.current = { key: "g", ts: now };
            else buffer.current.key = null;
        }

        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [navigate, location.pathname, onOpenCheatSheet]);
}
